import React, { useState, useEffect, useMemo, useRef } from 'react';
import { FiEdit2, FiX, FiPlus, FiFileText, FiEye, FiArrowLeft, FiStar } from 'react-icons/fi';
import {
  getWarehouses, createWarehouse, updateWarehouse, deleteWarehouse,
  getWarehouseStock, getFamilies, updateProductPrices
} from '../../../services/api';
import '../../../styles/ModuleTable.css';

const BLANK = { name: '', address: '', is_default: false, allow_negative_stock: false };

/* ─── Exportar Excel ──────────────────────────────────────────────── */
function exportToExcel(headers, rows, filename) {
  let html = `<table><thead><tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr></thead><tbody>`;
  rows.forEach(r => { html += `<tr>${r.map(c => `<td>${c ?? ''}</td>`).join('')}</tr>`; });
  html += '</tbody></table>';
  const blob = new Blob(['﻿' + html], { type: 'application/vnd.ms-excel' });
  const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
  a.download = `${filename}.xls`; a.click(); URL.revokeObjectURL(a.href);
}

/* ─── Paginación ─────────────────────────────────────────────────── */
function Paginacion({ page, totalPages, perPage, setPage, setPerPage }) {
  return (
    <div className="mt-pagination">
      <span className="pg-info">({page} de {totalPages})</span>
      <button onClick={() => setPage(1)} disabled={page === 1}>|&lt;</button>
      <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>&lt;&lt;</button>
      <span className="pg-current">{page}</span>
      <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>&gt;&gt;</button>
      <button onClick={() => setPage(totalPages)} disabled={page === totalPages}>&gt;|</button>
      <select value={perPage} onChange={e => { setPerPage(+e.target.value); setPage(1); }}>
        {[10, 20, 50].map(n => <option key={n} value={n}>{n}</option>)}
      </select>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   CELDA EDITABLE INLINE
══════════════════════════════════════════════════════════════════ */
function EditableCell({ value, onSave, prefix = '', decimals = 2, style = {} }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal]         = useState('');
  const [saving, setSaving]   = useState(false);
  const inputRef              = useRef(null);

  const startEdit = () => {
    setVal(parseFloat(value || 0).toString());
    setEditing(true);
    setTimeout(() => inputRef.current?.select(), 0);
  };

  const cancel = () => setEditing(false);

  const save = async () => {
    const num = parseFloat(val);
    if (isNaN(num) || num < 0) { cancel(); return; }
    if (num === parseFloat(value || 0)) { cancel(); return; }
    setSaving(true);
    try {
      await onSave(num);
      setEditing(false);
    } catch (e) {
      alert('Error al guardar: ' + (e.response?.data?.error || e.message));
    } finally { setSaving(false); }
  };

  const onKeyDown = e => {
    if (e.key === 'Enter')  save();
    if (e.key === 'Escape') cancel();
  };

  if (editing) {
    return (
      <td style={{ padding: '2px 4px' }}>
        <input
          ref={inputRef}
          type="number"
          step={decimals === 0 ? '1' : '0.01'}
          min="0"
          value={val}
          onChange={e => setVal(e.target.value)}
          onBlur={save}
          onKeyDown={onKeyDown}
          disabled={saving}
          style={{
            width: '100%', padding: '3px 5px', fontSize: 12,
            border: '2px solid #0ea5e9', borderRadius: 4,
            outline: 'none', background: '#fff', color: '#1e293b'
          }}
        />
      </td>
    );
  }

  return (
    <td
      onClick={startEdit}
      title="Clic para editar"
      style={{
        cursor: 'pointer', padding: '6px 12px',
        borderBottom: '1px solid #f1f5f9',
        ...style
      }}
    >
      <span style={{ borderBottom: '1px dashed #cbd5e1', paddingBottom: 1 }}>
        {prefix}{parseFloat(value || 0).toFixed(decimals)}
      </span>
    </td>
  );
}

/* ══════════════════════════════════════════════════════════════════
   VISTA DE STOCK DE UN ALMACÉN
══════════════════════════════════════════════════════════════════ */
function StockView({ warehouse, businessId, onBack }) {
  const [stock, setStock]               = useState([]);
  const [families, setFamilies]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState('');
  const [filterFamily, setFilterFamily] = useState('');
  const [page, setPage]                 = useState(1);
  const [perPage, setPerPage]           = useState(10);

  useEffect(() => {
    getFamilies(businessId).then(r => setFamilies(r.data.data || [])).catch(() => {});
    loadStock();
  }, []); // eslint-disable-line

  const loadStock = async () => {
    setLoading(true);
    try {
      const res = await getWarehouseStock(warehouse.id, businessId);
      setStock(res.data.data || []);
    } catch (e) {
      alert('Error: ' + (e.response?.data?.error || e.message));
    } finally { setLoading(false); }
  };

  // Actualiza el campo localmente sin recargar toda la tabla
  const updateLocal = (id, field, val) =>
    setStock(prev => prev.map(r => r.id === id ? { ...r, [field]: val } : r));

  const savePrices = (row, field) => async (newVal) => {
    const payload = {
      unit_price:  field === 'unit_price'  ? newVal : parseFloat(row.unit_price  || 0),
      cost_price:  field === 'cost_price'  ? newVal : parseFloat(row.cost_price  || 0),
      min_stock:   field === 'min_stock'   ? newVal : parseFloat(row.min_stock   || 0),
    };
    await updateProductPrices(row.id, businessId, payload);
    updateLocal(row.id, field, newVal);
  };

  const filtered = useMemo(() => {
    return stock.filter(r => {
      if (filterFamily && String(r.family_id) !== String(filterFamily)) return false;
      if (search) {
        const s = search.toUpperCase();
        return (r.name    || '').toUpperCase().includes(s)
            || (r.sku     || '').toUpperCase().includes(s);
      }
      return true;
    });
  }, [stock, search, filterFamily]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const paged = filtered.slice((page - 1) * perPage, page * perPage);

  const stockBadge = (val, min) => {
    const v = parseFloat(val || 0), m = parseFloat(min || 0);
    if (v <= 0) return <span style={{ color: '#ef4444', fontWeight: 700 }}>{v}</span>;
    if (v <= m) return <span style={{ color: '#f59e0b', fontWeight: 700 }}>{v}</span>;
    return <span style={{ color: '#16a34a', fontWeight: 600 }}>{v}</span>;
  };

  return (
    <div className="mt-container">
      <div className="mt-toolbar">
        <div className="mt-toolbar-left">
          <button className="btn-nuevo" style={{ background: '#64748b' }} onClick={onBack}>
            <FiArrowLeft size={12} /> Volver
          </button>
          <span className="mt-title" style={{ marginLeft: 8 }}>
            Stock — {warehouse.name}
            {!!warehouse.is_default && <FiStar size={12} style={{ marginLeft: 6, color: '#f59e0b' }} title="Predeterminado" />}
          </span>
        </div>
        <div className="mt-search-global">
          <span>Buscar</span>
          <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
        </div>
      </div>

      <div className="mt-export-bar" style={{ gap: 10 }}>
        <select
          style={{ padding: '4px 8px', border: '1px solid #e2e8f0', borderRadius: 5, fontSize: 12 }}
          value={filterFamily}
          onChange={e => { setFilterFamily(e.target.value); setPage(1); }}
        >
          <option value="">Todas las familias</option>
          {families.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
        <button className="btn-excel" onClick={() => exportToExcel(
          ['CÓD.', 'PRODUCTO', 'FAMILIA', 'STOCK', 'UNIDADES', 'U.M', 'P.COSTO', 'P.VENTA', 'MÍN.'],
          filtered.map(r => [r.sku, r.name, r.family_name, r.stock,
            r.special_condition ? r.stock_unidad : '', r.unit_measure,
            r.cost_price, r.unit_price, r.min_stock]),
          `Stock_${warehouse.name}`
        )}>
          <span>🐦</span> Excel
        </button>
        <Paginacion page={page} totalPages={totalPages} perPage={perPage} setPage={setPage} setPerPage={setPerPage} />
        <span style={{ marginLeft: 'auto', fontSize: 11, color: '#94a3b8' }}>
          {filtered.length} productos &nbsp;·&nbsp;
          <span style={{ color: '#0ea5e9' }}>clic en precio o mínimo para editar</span>
        </span>
      </div>

      <div className="mt-table-wrap">
        {loading ? <div className="mt-loading">Cargando stock...</div> : (
          <table className="mt-table mt-table-fixed">
            <colgroup>
              <col style={{ width: '9%' }} />
              <col style={{ width: '25%' }} />
              <col style={{ width: '12%' }} />
              <col style={{ width: '8%' }} />
              <col style={{ width: '7%' }} />
              <col style={{ width: '5%' }} />
              <col style={{ width: '9%' }} />
              <col style={{ width: '9%' }} />
              <col style={{ width: '7%' }} />
            </colgroup>
            <thead>
              <tr className="mt-head-row">
                <th>Cód. Prod.</th>
                <th>Producto</th>
                <th>Familia</th>
                <th>Stock</th>
                <th>Unidades</th>
                <th>U.M</th>
                <th title="Clic para editar">P. Costo ✎</th>
                <th title="Clic para editar">P. Venta ✎</th>
                <th title="Clic para editar">Mín. ✎</th>
              </tr>
            </thead>
            <tbody>
              {paged.length === 0
                ? <tr className="mt-empty"><td colSpan={9}>Sin productos</td></tr>
                : paged.map((row, i) => (
                  <tr key={row.id} className={i % 2 === 0 ? 'mt-row-even' : 'mt-row-odd'}>
                    <td>{row.sku}</td>
                    <td className="td-truncate" title={row.name}>{row.name}</td>
                    <td>{row.family_name || ''}</td>
                    <td>{stockBadge(row.stock, row.min_stock)}</td>
                    <td>{row.special_condition ? row.stock_unidad : '—'}</td>
                    <td>{row.unit_measure}</td>

                    <EditableCell
                      value={row.cost_price}
                      prefix="S/ "
                      onSave={savePrices(row, 'cost_price')}
                    />
                    <EditableCell
                      value={row.unit_price}
                      prefix="S/ "
                      onSave={savePrices(row, 'unit_price')}
                    />
                    <EditableCell
                      value={row.min_stock}
                      decimals={3}
                      onSave={savePrices(row, 'min_stock')}
                    />
                  </tr>
                ))
              }
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   LISTA DE ALMACENES (CRUD)
══════════════════════════════════════════════════════════════════ */
export default function AlmacenMaintenance({ businessId }) {
  const [data, setData]         = useState([]);
  const [loading, setLoading]   = useState(false);
  const [modal, setModal]       = useState(false);
  const [editId, setEditId]     = useState(null);
  const [form, setForm]         = useState(BLANK);
  const [saving, setSaving]     = useState(false);
  const [viewStock, setViewStock] = useState(null); // almacén seleccionado para ver stock

  useEffect(() => { load(); }, [businessId]); // eslint-disable-line

  const load = async () => {
    setLoading(true);
    try {
      const res = await getWarehouses(businessId);
      setData(res.data.data || []);
    } catch (e) {
      alert('Error al cargar almacenes: ' + (e.response?.data?.error || e.message));
    } finally { setLoading(false); }
  };

  const openNew = () => {
    setForm({ ...BLANK });
    setEditId(null);
    setModal(true);
  };

  const openEdit = row => {
    setForm({
      name: row.name || '', address: row.address || '',
      is_default: Boolean(row.is_default),
      allow_negative_stock: Boolean(row.allow_negative_stock),
    });
    setEditId(row.id);
    setModal(true);
  };

  const handleDelete = async id => {
    if (!window.confirm('¿Desea eliminar este almacén?')) return;
    try {
      await deleteWarehouse(id, businessId);
      load();
    } catch (e) { alert('Error: ' + (e.response?.data?.error || e.message)); }
  };

  const handleSave = async e => {
    e.preventDefault();
    setSaving(true);
    try {
      editId
        ? await updateWarehouse(editId, businessId, form)
        : await createWarehouse(businessId, form);
      setModal(false);
      load();
    } catch (e) {
      alert('Error: ' + (e.response?.data?.error || e.message));
    } finally { setSaving(false); }
  };

  const f = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  // Si hay un almacén seleccionado para ver stock, mostrar esa vista
  if (viewStock) {
    return <StockView warehouse={viewStock} businessId={businessId} onBack={() => setViewStock(null)} />;
  }

  return (
    <div className="mt-container">
      <div className="mt-toolbar">
        <div className="mt-toolbar-left">
          <button className="btn-nuevo" onClick={openNew}><FiPlus size={12} /> Nuevo</button>
        </div>
        <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 8 }}>
          Los productos se agregan automáticamente a cada almacén
        </span>
      </div>

      <div className="mt-table-wrap">
        {loading ? <div className="mt-loading">Cargando...</div> : (
          <table className="mt-table mt-table-fixed">
            <colgroup>
              <col style={{ width: '24%' }} />
              <col style={{ width: '30%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: 38 }} />
              <col style={{ width: 38 }} />
              <col style={{ width: 38 }} />
            </colgroup>
            <thead>
              <tr className="mt-head-row">
                <th>Nombre</th>
                <th>Dirección</th>
                <th>Predeterminado</th>
                <th>Stock Negativo</th>
                <th>Productos</th>
                <th></th>
                <th></th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data.length === 0
                ? <tr className="mt-empty"><td colSpan={8}>Sin almacenes</td></tr>
                : data.map((row, i) => (
                  <tr key={row.id} className={i % 2 === 0 ? 'mt-row-even' : 'mt-row-odd'}>
                    <td>
                      {!!row.is_default && (
                        <FiStar size={11} style={{ color: '#f59e0b', marginRight: 5, verticalAlign: 'middle' }} title="Predeterminado" />
                      )}
                      {row.name}
                    </td>
                    <td className="td-truncate" title={row.address}>{row.address || '—'}</td>
                    <td style={{ textAlign: 'center' }}>
                      {!!row.is_default
                        ? <span style={{ color: '#16a34a', fontWeight: 700, fontSize: 11 }}>SÍ</span>
                        : <span style={{ color: '#94a3b8', fontSize: 11 }}>No</span>}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {!!row.allow_negative_stock
                        ? <span style={{ color: '#16a34a', fontWeight: 700, fontSize: 11 }}>SÍ</span>
                        : <span style={{ color: '#94a3b8', fontSize: 11 }}>No</span>}
                    </td>
                    <td style={{ textAlign: 'center', fontWeight: 600 }}>{row.total_productos}</td>
                    <td className="td-actions">
                      <button className="btn-row-edit" onClick={() => setViewStock(row)} title="Ver Stock">
                        <FiEye size={13} />
                      </button>
                    </td>
                    <td className="td-actions">
                      <button className="btn-row-edit" onClick={() => openEdit(row)} title="Editar">
                        <FiEdit2 size={13} />
                      </button>
                    </td>
                    <td className="td-actions">
                      <button className="btn-row-del" onClick={() => handleDelete(row.id)} title="Eliminar">
                        <FiX size={14} />
                      </button>
                    </td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        )}
      </div>

      {/* Modal Nuevo / Editar */}
      {modal && (
        <div className="mt-modal-overlay">
          <div className="mt-modal" style={{ maxWidth: 500 }}>
            <div className="mt-modal-header">
              <span>{editId ? 'Editar Almacén' : 'Nuevo Almacén'}</span>
              <button className="btn-modal-close" onClick={() => setModal(false)}>✕</button>
            </div>
            <form onSubmit={handleSave}>
              <div className="mt-modal-body">
                <div className="mt-form-grid" style={{ gridTemplateColumns: '1fr' }}>

                  <div className="mt-field">
                    <label>Nombre <span className="req">*</span></label>
                    <input value={form.name} onChange={f('name')}
                      onBlur={e => setForm(p => ({ ...p, name: p.name.toUpperCase() }))}
                      required />
                  </div>

                  <div className="mt-field">
                    <label>Dirección</label>
                    <input value={form.address} onChange={f('address')} placeholder="Opcional" />
                  </div>

                  <div className="mt-field" style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                    <input
                      type="checkbox"
                      id="is_default"
                      checked={form.is_default}
                      onChange={e => setForm(p => ({ ...p, is_default: e.target.checked }))}
                      style={{ width: 16, height: 16, cursor: 'pointer' }}
                    />
                    <label htmlFor="is_default" style={{ cursor: 'pointer', marginBottom: 0 }}>
                      Almacén Predeterminado
                      <span style={{ fontSize: 10, color: '#94a3b8', marginLeft: 6 }}>
                        (recibe el stock inicial de nuevos productos)
                      </span>
                    </label>
                  </div>

                  <div className="mt-field" style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                    <input
                      type="checkbox"
                      id="allow_negative_stock"
                      checked={form.allow_negative_stock}
                      onChange={e => setForm(p => ({ ...p, allow_negative_stock: e.target.checked }))}
                      style={{ width: 16, height: 16, cursor: 'pointer' }}
                    />
                    <label htmlFor="allow_negative_stock" style={{ cursor: 'pointer', marginBottom: 0 }}>
                      Permitir Stock Negativo
                      <span style={{ fontSize: 10, color: '#94a3b8', marginLeft: 6 }}>
                        (permite vender/dar salida aunque no haya stock suficiente)
                      </span>
                    </label>
                  </div>

                </div>
              </div>
              <div className="mt-modal-footer">
                <button type="submit" className="btn-modificar" disabled={saving}>
                  {saving ? 'Guardando...' : (editId ? 'Modificar' : 'Guardar')}
                </button>
                <button type="button" className="btn-salir" onClick={() => setModal(false)}>✕ Salir</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
